import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import {
  getTopics,
  createTopic,
  updateTopic,
  deleteTopic,
} from "../services/TopicService";
import { getLearnCounts } from "../services/VocabService";

const VocabularyContext = createContext(null);

/**
 * Global state provider for the vocabulary module.
 *
 * Responsibilities:
 *  - Owns the `topics` list with per-topic counts
 *    (wordCount, learnedCount, partialCount, noneCount).
 *  - Exposes `allStats` as a derived/computed value — the sum
 *    of every topic's counts — so "Tất cả từ vựng" never needs
 *    a separate API call.
 *  - Exposes `bumpTopicCounts(topicId, delta)` so vocab operations
 *    (add / remove / status-change) can optimistically update the
 *    sidebar counts and progress bar in real-time.
 */
export function VocabularyProvider({ children }) {
  const [topics, setTopics] = useState([]);
  const [topicLoading, setTopicLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null); // null = "Tất cả"

  // Counts for words that have no topicId (topicId === null)
  const [unassignedCounts, setUnassignedCounts] = useState({
    wordCount: 0, learnedCount: 0, partialCount: 0, noneCount: 0,
  });

  // ── Fetch topics (initial load only) ───────────────────────────
  const fetchTopics = useCallback(async () => {
    try {
      setTopicLoading(true);
      // Fetch topics (with per-topic counts) and the global totals in parallel.
      // The global totals include words that have no topic (unassigned).
      const [topicsRes, globalRes] = await Promise.all([
        getTopics(),
        getLearnCounts(""),
      ]);
      const fetchedTopics = topicsRes?.data ?? [];
      setTopics(fetchedTopics);

      // Compute unassigned counts = global - sum of all topic counts
      const g = globalRes?.data ?? {};
      const topicTotal   = fetchedTopics.reduce((s, t) => s + (t.wordCount    ?? 0), 0);
      const topicLearned = fetchedTopics.reduce((s, t) => s + (t.learnedCount ?? 0), 0);
      const topicPartial = fetchedTopics.reduce((s, t) => s + (t.partialCount ?? 0), 0);
      setUnassignedCounts({
        wordCount:    (g.total        ?? 0) - topicTotal,
        learnedCount: (g.learnedCount ?? 0) - topicLearned,
        partialCount: (g.partialCount ?? 0) - topicPartial,
        noneCount:    (g.noneCount    ?? 0) - (topicTotal - topicLearned - topicPartial),
      });
    } catch {
      toast.error("Không thể tải danh sách chủ đề");
    } finally {
      setTopicLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  // ── Derived: aggregate stats across ALL topics + unassigned words ─
  // "Tất cả từ vựng" progress is computed from topic data — no extra API call.
  const allStats = useMemo(
    () => ({
      total:        topics.reduce((s, t) => s + (t.wordCount    ?? 0), 0) + unassignedCounts.wordCount,
      learnedCount: topics.reduce((s, t) => s + (t.learnedCount ?? 0), 0) + unassignedCounts.learnedCount,
      partialCount: topics.reduce((s, t) => s + (t.partialCount ?? 0), 0) + unassignedCounts.partialCount,
      noneCount:    topics.reduce((s, t) => s + (t.noneCount    ?? 0), 0) + unassignedCounts.noneCount,
    }),
    [topics, unassignedCounts]
  );

  // ── Optimistic counter bump ─────────────────────────────────────
  // Used by vocab operations to update sidebar + progress without
  // re-fetching topics from the server.
  //
  // topicId === null means the word has no topic (unassigned).
  // delta = { wordCount?: ±n, learnedCount?: ±n, partialCount?: ±n, noneCount?: ±n }
  const bumpTopicCounts = useCallback((topicId, delta) => {
    if (!topicId) {
      // Word is not assigned to any topic — update the unassigned bucket
      setUnassignedCounts((prev) => ({
        wordCount:    (prev.wordCount    ?? 0) + (delta.wordCount    ?? 0),
        learnedCount: (prev.learnedCount ?? 0) + (delta.learnedCount ?? 0),
        partialCount: (prev.partialCount ?? 0) + (delta.partialCount ?? 0),
        noneCount:    (prev.noneCount    ?? 0) + (delta.noneCount    ?? 0),
      }));
      return;
    }
    setTopics((prev) =>
      prev.map((t) =>
        t._id === topicId
          ? {
              ...t,
              wordCount:    (t.wordCount    ?? 0) + (delta.wordCount    ?? 0),
              learnedCount: (t.learnedCount ?? 0) + (delta.learnedCount ?? 0),
              partialCount: (t.partialCount ?? 0) + (delta.partialCount ?? 0),
              noneCount:    (t.noneCount    ?? 0) + (delta.noneCount    ?? 0),
            }
          : t
      )
    );
  }, []);

  // ── Topic CRUD ──────────────────────────────────────────────────
  const addTopic = useCallback(async (name, icon = "") => {
    try {
      const res = await createTopic(name, icon);
      setTopics((prev) => [
        ...prev,
        { ...res.data, wordCount: 0, learnedCount: 0, partialCount: 0, noneCount: 0 },
      ]);
      toast.success("Tạo chủ đề thành công");
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data?.message || "Tạo chủ đề thất bại");
      throw err;
    }
  }, []);

  const renameTopic = useCallback(async (id, name, icon) => {
    try {
      const res = await updateTopic(id, name, icon);
      setTopics((prev) =>
        prev.map((t) => (t._id === id ? { ...t, name: res.data.name, icon: res.data.icon ?? t.icon } : t))
      );
      toast.success("Đổi tên thành công");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Đổi tên thất bại");
      throw err;
    }
  }, []);

  const removeTopic = useCallback(async (id) => {
    try {
      await deleteTopic(id);
      setTopics((prev) => prev.filter((t) => t._id !== id));
      setSelectedId((prev) => (prev === id ? null : prev));
      toast.success("Xóa chủ đề thành công");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Xóa chủ đề thất bại");
    }
  }, []);

  const reorderTopics = useCallback((newOrder) => {
    setTopics(newOrder);
  }, []);

  const value = {
    topics,
    topicLoading,
    selectedId,
    setSelectedId,
    allStats,
    bumpTopicCounts,
    addTopic,
    renameTopic,
    removeTopic,
    refetchTopics: fetchTopics,
    reorderTopics,
  };

  return (
    <VocabularyContext.Provider value={value}>
      {children}
    </VocabularyContext.Provider>
  );
}

export function useVocabularyContext() {
  const ctx = useContext(VocabularyContext);
  if (!ctx) {
    throw new Error("useVocabularyContext must be used within a VocabularyProvider");
  }
  return ctx;
}
